import { useState, useCallback } from 'react'

/**
 * Reads a node selection from sessionStorage and keeps it in sync.
 * The stored value persists across page refreshes within the same tab.
 * Each tab has its own sessionStorage, so multiple tabs/windows won't interfere.
 *
 * @param storageKey Full sessionStorage key (e.g. `report-selection-${mindmapId}`)
 */
export function useSelectionFromStorage(storageKey: string | undefined): {
  selectionIds: Set<string> | null
  hasSelection: boolean
  clearSelection: () => void
} {
  const [selectionIds, setSelectionIds] = useState<Set<string> | null>(() => {
    if (!storageKey) return null
    const stored = sessionStorage.getItem(storageKey)
    if (!stored) return null
    const ids = JSON.parse(stored) as string[]
    return ids.length > 0 ? new Set(ids) : null
  })

  const clearSelection = useCallback(() => {
    if (storageKey) sessionStorage.removeItem(storageKey)
    setSelectionIds(null)
  }, [storageKey])

  return {
    selectionIds,
    hasSelection: selectionIds !== null,
    clearSelection,
  }
}
